import { useState } from 'react';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { env } from '@shared/config/env';
import { mapPasswordApiError, type PasswordApiFailure } from '@entities/auth';

export const useResetPassword = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkToken = async (
    token: string,
  ): Promise<
    | { valid: true; requires2fa: boolean }
    | { valid: false; failure: PasswordApiFailure | null }
  > => {
    setIsChecking(true);

    try {
      const authApi = createAuthApi(createApiClient({ baseURL: env.API_BASE_URL }));
      const response = await authApi.checkResetPassword({ token });
      if (!response.valid) {
        return { valid: false, failure: null };
      }
      return { valid: true, requires2fa: Boolean(response.requires2fa) };
    } catch (error: unknown) {
      return { valid: false, failure: mapPasswordApiError(error) };
    } finally {
      setIsChecking(false);
    }
  };

  const submitPassword = async (payload: {
    token: string;
    password: string;
    code?: string;
  }): Promise<{ success: true } | { success: false; failure: PasswordApiFailure | null }> => {
    setIsSubmitting(true);

    try {
      const authApi = createAuthApi(createApiClient({ baseURL: env.API_BASE_URL }));
      await authApi.resetPassword({
        token: payload.token,
        password: payload.password,
        ...(payload.code ? { code: payload.code } : {}),
      });
      return { success: true };
    } catch (error: unknown) {
      return { success: false, failure: mapPasswordApiError(error) };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { checkToken, submitPassword, isChecking, isSubmitting };
};
